import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import TamamlayiciSaglikSeo from './TamamlayiciSaglikSeo';

// Şirket logoları mapping
const companyLogos = {
  'Neova': '/insurance-companies/neovasigorta.jpg',
  'Ray': '/insurance-companies/raysigorta.jpg',
  'Doga': '/insurance-companies/dogasigorta.jpg',
  'Doğa': '/insurance-companies/dogasigorta.jpg',
  'Magdeburger': '/insurance-companies/magdeburgerlogo.jpg',
  'Ak': '/insurance-companies/aksigorta.jpg',
  'Mapfre': '/insurance-companies/mapfre.png',
  'Axa': '/insurance-companies/axasigorta.jpg',
  'Türkiye': '/insurance-companies/turkiyesigorta.jpg',
  'Anadolu': '/insurance-companies/anadolusigorta.jpg',
  'Allianz': '/insurance-companies/allianzsigorta.jpg',
  'HDI': '/insurance-companies/hdisigorta.jpg',
  'Hepiyi': '/insurance-companies/hepiyisigorta.jpg',
  'Koru': '/insurance-companies/Koru-Logo.png',
  'Quick': '/insurance-companies/quick.png',
  'Sompo': '/insurance-companies/somposigorta.jpg',
  'Ankara': '/insurance-companies/ankarasigortalogo.png',
  'Referans': '/insurance-companies/referans.png',
  'Corpus': '/insurance-companies/corpus.png',
  'Türk Nippon': '/insurance-companies/turknipponsigorta.jpg'
};

// Loading mesajları
const loadingMessages = [
  { text: "Sigorta şirketlerinden teklifler alınıyor...", icon: "🔍" },
  { text: "En uygun fiyatları karşılaştırıyoruz...", icon: "💰" },
  { text: "Neredeyse hazır, biraz daha sabır...", icon: "⏳" },
  { text: "Son kontroller yapılıyor...", icon: "✨" }
];

// TSS Faydaları
const tssBenefits = [
  { icon: "🏥", title: "Geniş Hastane Ağı", desc: "Türkiye genelinde 500+ özel hastanede geçerli" },
  { icon: "👨‍⚕️", title: "10 Ayakta Tedavi", desc: "Yılda 10 kez doktor muayenesi hakkı" },
  { icon: "💊", title: "İlaç Desteği", desc: "Reçeteli ilaçlarınızda indirim" },
  { icon: "🔬", title: "Tetkik & Tahlil", desc: "Laboratuvar işlemlerinde tam kapsam" },
  { icon: "🩺", title: "Yatarak Tedavi", desc: "Ameliyat ve hastane yatışlarında %100 kapsam" },
  { icon: "🚑", title: "Acil Durumlar", desc: "7/24 acil sağlık hizmetleri" }
];

// Loading komponenti
const LoadingScreen = ({ quotesCount }) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [benefitIndex, setBenefitIndex] = useState(0);

  useEffect(() => {
    const messageTimer = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % loadingMessages.length);
    }, 3000);

    const benefitTimer = setInterval(() => {
      setBenefitIndex(prev => (prev + 1) % tssBenefits.length);
    }, 2500);

    return () => {
      clearInterval(messageTimer);
      clearInterval(benefitTimer);
    };
  }, []);

  return (
    <div className="flex flex-col items-center justify-center py-8">
      {/* Ana animasyon */}
      <div className="relative mb-8">
        {/* Dönen dış halka */}
        <div className="w-32 h-32 rounded-full border-4 border-blue-100 border-t-blue-500 animate-spin"></div>
        
        {/* İç içerik */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">{quotesCount}</div>
            <div className="text-xs text-gray-500">teklif</div>
          </div>
        </div>
      </div>

      {/* Loading mesajı */}
      <div className="text-center mb-8 h-16">
        <div className="flex items-center justify-center gap-2 text-lg font-medium text-gray-700 animate-pulse">
          <span className="text-2xl">{loadingMessages[messageIndex].icon}</span>
          <span>{loadingMessages[messageIndex].text}</span>
        </div>
      </div>

      {/* Fayda kartları carousel */}
      <div className="w-full max-w-sm">
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-100 shadow-sm">
          <div className="text-center">
            <div className="text-4xl mb-3">{tssBenefits[benefitIndex].icon}</div>
            <h3 className="font-semibold text-gray-800 mb-1">{tssBenefits[benefitIndex].title}</h3>
            <p className="text-sm text-gray-600">{tssBenefits[benefitIndex].desc}</p>
          </div>
          
          {/* Nokta göstergeleri */}
          <div className="flex justify-center gap-1.5 mt-4">
            {tssBenefits.map((_, idx) => (
              <div
                key={idx}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  idx === benefitIndex ? 'bg-blue-500 w-4' : 'bg-blue-200'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Alt bilgi */}
      <p className="text-sm text-gray-400 mt-6 text-center">
        Tamamlayıcı Sağlık Sigortası ile SGK'lı hastanelerde<br/>fark ücreti ödemeden tedavi olun
      </p>
    </div>
  );
};

const TamamlayiciSaglik = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    tc: '',
    dogumTarihi: '',
    boy: '',
    kilo: '',
    ailePolice: false,
    telefon: '',
    kvkk: true
  });

  const [validationErrors, setValidationErrors] = useState({});
  
  // Canlı teklif state'leri
  const [loading, setLoading] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [showQuotes, setShowQuotes] = useState(false);

  const agents = [
    { name: 'Zehra', title: 'Sağlık Sigortası Uzmanı', image: '👩‍⚕️', testimonial: "Size en uygun tamamlayıcı sağlık sigortasını sunalım." },
    { name: 'Deniz', title: 'Sigorta Danışmanı', image: '👨‍💼', testimonial: "Aileniz için en iyi sağlık güvencesi." },
    { name: 'Selin', title: 'TSS Uzmanı', image: '👩‍💼', testimonial: "Uygun primlerle sağlığınızı koruyalım." },
    { name: 'Can', title: 'Sağlık Sigortası Danışmanı', image: '👨‍⚕️', testimonial: "Kapsamlı sağlık güvencesi için yanınızdayız." }
  ];

  const [activeAgent, setActiveAgent] = useState(0);

  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * agents.length);
    setActiveAgent(randomIndex);

    const timer = setInterval(() => {
      setActiveAgent((prev) => (prev + 1) % agents.length);
    }, 3000);

    return () => clearInterval(timer);
  }, []);

  const validateForm = () => {
    const errors = {};
    
    if (!formData.tc || formData.tc.length !== 11 || !/^\d+$/.test(formData.tc)) {
      errors.tc = 'Geçerli bir TC Kimlik Numarası giriniz';
    }

    if (!formData.dogumTarihi) {
      errors.dogumTarihi = 'Doğum tarihi gereklidir';
    }

    if (!formData.boy || formData.boy < 100 || formData.boy > 250) {
      errors.boy = 'Geçerli bir boy değeri giriniz';
    }

    if (!formData.kilo || formData.kilo < 30 || formData.kilo > 200) {
      errors.kilo = 'Geçerli bir kilo değeri giriniz';
    }

    const phoneRegex = /^[0-9]{10,11}$/;
    if (!formData.telefon || !phoneRegex.test(formData.telefon.replace(/\D/g, ''))) {
      errors.telefon = 'Geçerli bir telefon numarası giriniz';
    }

    if (!formData.kvkk) {
      errors.kvkk = 'KVKK metnini kabul etmelisiniz';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Canlı teklif al
  const fetchLiveQuotes = async () => {
    setLoading(true);
    setQuotes([]);
    setCompleted(false);
    setShowQuotes(true);
    setError('');

    const queryData = {
      PersonIdentityNo: formData.tc,
      BirthDay: new Date(formData.dogumTarihi).toISOString(),
      PhoneNumber: formData.telefon.replace(/\D/g, ''),
      Height: formData.boy,
      Weight: formData.kilo,
      NetworkType: 1,
      PlanType: 0,
      BirthCover: false
    };

    try {
      const response = await fetch('/saglik-teklif-stream.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(queryData)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Teklif alınamadı');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          setLoading(false);
          setCompleted(true);
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.error) {
                setError(data.error);
                setLoading(false);
                return;
              }

              if (data.completed) {
                setLoading(false);
                setCompleted(true);
                return;
              }

              if (data.quote && data.quote.companyCode !== 'SYSTEM') {
                setQuotes(prev => {
                  const idx = prev.findIndex(q => q.companyCode === data.quote.companyCode);
                  if (idx >= 0) {
                    const updated = [...prev];
                    updated[idx] = data.quote;
                    return updated;
                  }
                  return [...prev, data.quote];
                });
              }
            } catch (e) {
              console.log('Parse error:', e);
            }
          }
        }
      }
    } catch (err) {
      setError(err.message || 'Bir hata oluştu');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const response = await fetch(`${window.location.protocol}//${window.location.host}/tamamlayici-saglik.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Bir hata oluştu');
      }

      await fetchLiveQuotes();

    } catch (err) {
      setError(err.message);
      console.error('Form gönderme hatası:', err);
    }
  };

  // Fiyat formatlama
  const formatPrice = (price) => {
    if (!price) return null;
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Taksit hesapla
  const calculateInstallment = (price) => {
    if (!price) return null;
    return formatPrice(Math.ceil(price / 9));
  };

  // Teklifleri fiyata göre sırala
  const sortedQuotes = [...quotes].sort((a, b) => {
    if (!a.premium && !b.premium) return 0;
    if (!a.premium) return 1;
    if (!b.premium) return -1;
    return a.premium - b.premium;
  });

  // Logo bul
  const getCompanyLogo = (companyName) => {
    if (!companyName) return null;
    
    if (companyLogos[companyName]) {
      return companyLogos[companyName];
    }
    
    const key = Object.keys(companyLogos).find(k => 
      companyName.toLowerCase().includes(k.toLowerCase()) ||
      k.toLowerCase().includes(companyName.toLowerCase())
    );
    
    return key ? companyLogos[key] : null;
  };

  // Fiyatlı teklif sayısı
  const validQuotesCount = quotes.filter(q => q.premium).length;

  return (
    <div className="overflow-x-hidden">
      <Helmet>
        <title>Tamamlayıcı Sağlık Sigortası | SigortamOlsun</title>
        <meta name="description" content="En uygun tamamlayıcı sağlık sigortası tekliflerini karşılaştırın." />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="canonical" href="https://sigortamolsun.com/tamamlayici-saglik" />
      </Helmet>

      <div className="px-4 py-8">
        <div className="max-w-4xl mx-auto">
          
          {/* Teklifler gösteriliyorsa */}
          {showQuotes ? (
            <div>
              {/* Loading durumu - teklifler henüz hazır değilse */}
              {loading && validQuotesCount === 0 ? (
                <LoadingScreen quotesCount={quotes.length} />
              ) : (
                <>
                  {/* Üst bilgi bandı */}
                  <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-4 mb-6 text-white">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <h2 className="text-lg font-semibold">Tamamlayıcı Sağlık Sigortası Teklifleri</h2>
                        <p className="text-blue-100 text-sm mt-1">10 Ayakta Tedavi + Yatarak Tedavi • Geniş Hastane Ağı</p>
                      </div>
                      <button
                        onClick={() => {
                          setShowQuotes(false);
                          setQuotes([]);
                          setCompleted(false);
                        }}
                        className="text-sm bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition"
                      >
                        ← Yeni Teklif Al
                      </button>
                    </div>
                  </div>

                  {/* Durum */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-gray-600 text-sm">
                      {validQuotesCount} teklif bulundu
                    </div>
                    {loading && (
                      <span className="text-sm text-blue-600 flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                        </svg>
                        Diğer teklifler alınıyor...
                      </span>
                    )}
                    {completed && (
                      <span className="text-sm text-green-600 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                        </svg>
                        Tamamlandı
                      </span>
                    )}
                  </div>

                  {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                      {error}
                    </div>
                  )}

                  {/* Teklif listesi */}
                  <div className="space-y-3">
                    {sortedQuotes.map((quote, index) => {
                      const hasPrice = quote.premium && quote.status !== 'error' && quote.status !== 'rejected';
                      const isError = quote.status === 'error' || quote.status === 'rejected';
                      const logoSrc = getCompanyLogo(quote.companyName);
                      const isFirst = index === 0 && hasPrice;

                      return (
                        <div
                          key={quote.companyCode || index}
                          className={`bg-white rounded-xl border transition-all ${
                            isError 
                              ? 'border-gray-200 opacity-50' 
                              : isFirst
                                ? 'border-green-300 shadow-md ring-2 ring-green-100'
                                : 'border-gray-200 hover:border-blue-200 hover:shadow-sm'
                          }`}
                        >
                          {/* En uygun teklif badge */}
                          {isFirst && (
                            <div className="bg-green-500 text-white text-xs font-medium px-3 py-1 rounded-t-xl">
                              ✓ En Uygun Teklif
                            </div>
                          )}
                          
                          <div className="p-4">
                            <div className="flex items-center gap-4">
                              {/* Logo */}
                              <div className="w-16 h-16 flex-shrink-0 bg-gray-50 rounded-lg flex items-center justify-center border border-gray-100 overflow-hidden">
                                {logoSrc ? (
                                  <img 
                                    src={logoSrc} 
                                    alt={quote.companyName} 
                                    className="max-w-full max-h-full object-contain p-1"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <span 
                                  className={`text-xl font-bold text-gray-400 ${logoSrc ? 'hidden' : 'flex'}`}
                                  style={{ display: logoSrc ? 'none' : 'flex' }}
                                >
                                  {quote.companyName?.charAt(0)}
                                </span>
                              </div>

                              {/* Şirket bilgisi */}
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-gray-800 text-lg">{quote.companyName}</div>
                                {hasPrice && (
                                  <div className="flex flex-wrap gap-2 mt-1">
                                    <span className="inline-flex items-center text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                                      10 Ayakta Tedavi
                                    </span>
                                    <span className="inline-flex items-center text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded">
                                      Geniş Hastane Ağı
                                    </span>
                                  </div>
                                )}
                                {isError && (
                                  <span className="text-red-500 text-sm">Teklif alınamadı</span>
                                )}
                              </div>

                              {/* Fiyat */}
                              {hasPrice ? (
                                <div className="text-right flex-shrink-0">
                                  <div className="text-2xl font-bold text-gray-800">
                                    {formatPrice(quote.premium)}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    9 taksit x {calculateInstallment(quote.premium)}
                                  </div>
                                </div>
                              ) : !isError && (
                                <div className="text-gray-300">
                                  <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                                  </svg>
                                </div>
                              )}
                            </div>

                            {/* Satın Al butonu */}
                            {hasPrice && (
                              <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                                <div className="text-xs text-gray-500">
                                  Yıllık toplam: {formatPrice(quote.premium)}
                                </div>
                                <button className={`px-5 py-2 rounded-lg text-sm font-medium transition ${
                                  isFirst 
                                    ? 'bg-green-500 hover:bg-green-600 text-white'
                                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                                }`}>
                                  Satın Al
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Alt bilgi */}
                  {completed && validQuotesCount > 0 && (
                    <div className="mt-6 bg-gray-50 rounded-xl p-5 border border-gray-200">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-2xl">📞</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-800">Yardıma mı ihtiyacınız var?</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Teklif detayları ve teminat karşılaştırması için uzmanlarımız sizi arasın.
                          </p>
                          <button className="mt-3 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition">
                            Beni Arayın
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            /* Mevcut form */
            <div className="max-w-md mx-auto">
              {/* Agent Tabloları */}
              <div className="relative h-32 mb-4">
                {agents.map((agent, index) => (
                  <div
                    key={agent.name}
                    className={`absolute w-full transition-all duration-500 transform
                      ${index === activeAgent ? 'scale-100 opacity-100' : 'scale-75 opacity-0'}
                      flex items-center gap-3 bg-blue-50 p-4 rounded-xl shadow-md border-2 border-blue-200`}
                    style={{
                      transform: `translateX(${(index - activeAgent) * 100}%) scale(${index === activeAgent ? 1 : 0.75})`,
                      zIndex: index === activeAgent ? 10 : 0
                    }}
                  >
                    <span className="text-2xl sm:text-3xl flex-shrink-0">{agent.image}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-blue-900">{agent.name}</div>
                      <div className="text-sm text-blue-600">{agent.title}</div>
                      <div className="text-sm italic text-gray-500 break-words">{agent.testimonial}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bilgilendirme */}
              <div className="text-center mb-6">
                <h1 className="text-xl sm:text-2xl font-semibold mb-4 whitespace-pre-line">
                  {'Merhaba,\nBen sağlık sigortaları uzmanı '}<span className="text-blue-600">{agents[activeAgent].name}</span>{'.\nHemen teklifinizi hazırlayalım.'}
                </h1>
                <p className="text-gray-600 text-sm">
                  Tamamlayıcı sağlık sigortası teklifi almak için bilgilerinizi girin.
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                  {error}
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="tel"
                    inputMode="numeric"
                    placeholder="TC Kimlik Numarası"
                    className={`w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600
                      ${validationErrors.tc ? 'border-red-500' : 'border-gray-300'}`}
                    maxLength="11"
                    value={formData.tc}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setFormData({ ...formData, tc: value });
                      if (validationErrors.tc) setValidationErrors({ ...validationErrors, tc: '' });
                    }}
                  />
                  {validationErrors.tc && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.tc}</p>
                  )}
                </div>

                <div>
                  <input
                    type="date"
                    placeholder="Doğum Tarihi"
                    className={`w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600
                      ${validationErrors.dogumTarihi ? 'border-red-500' : 'border-gray-300'}`}
                    value={formData.dogumTarihi}
                    onChange={(e) => {
                      setFormData({ ...formData, dogumTarihi: e.target.value });
                      if (validationErrors.dogumTarihi) setValidationErrors({ ...validationErrors, dogumTarihi: '' });
                    }}
                  />
                  {validationErrors.dogumTarihi && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.dogumTarihi}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <input
                      type="number"
                      placeholder="Boy (cm)"
                      className={`w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600
                        ${validationErrors.boy ? 'border-red-500' : 'border-gray-300'}`}
                      value={formData.boy}
                      onChange={(e) => {
                        setFormData({ ...formData, boy: e.target.value });
                        if (validationErrors.boy) setValidationErrors({ ...validationErrors, boy: '' });
                      }}
                    />
                    {validationErrors.boy && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.boy}</p>
                    )}
                  </div>
                  <div>
                    <input
                      type="number"
                      placeholder="Kilo (kg)"
                      className={`w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600
                        ${validationErrors.kilo ? 'border-red-500' : 'border-gray-300'}`}
                      value={formData.kilo}
                      onChange={(e) => {
                        setFormData({ ...formData, kilo: e.target.value });
                        if (validationErrors.kilo) setValidationErrors({ ...validationErrors, kilo: '' });
                      }}
                    />
                    {validationErrors.kilo && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.kilo}</p>
                    )}
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-100">
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={formData.ailePolice}
                      onChange={(e) => setFormData({ ...formData, ailePolice: e.target.checked })}
                      className="mt-1"
                    />
                    <div>
                      <label className="font-medium text-gray-900">Aile Poliçesi İster misiniz?</label>
                      <p className="text-sm text-gray-600">%25'e varan aile indirimi fırsatını kaçırmayın!</p>
                    </div>
                  </div>
                </div>

                <div>
                  <input
                    type="tel"
                    placeholder="Telefon Numarası"
                    className={`w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600
                      ${validationErrors.telefon ? 'border-red-500' : 'border-gray-300'}`}
                    value={formData.telefon}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setFormData({ ...formData, telefon: value });
                      if (validationErrors.telefon) setValidationErrors({ ...validationErrors, telefon: '' });
                    }}
                  />
                  {validationErrors.telefon && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.telefon}</p>
                  )}
                </div>

                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={formData.kvkk}
                    onChange={(e) => {
                      setFormData({ ...formData, kvkk: e.target.checked });
                      if (validationErrors.kvkk) setValidationErrors({ ...validationErrors, kvkk: '' });
                    }}
                    className={`mt-1 ${validationErrors.kvkk ? 'border-red-500' : ''}`}
                  />
                  <label className="text-sm text-gray-600">
                    <Link to="/kvkk" className="text-blue-600 hover:text-blue-700">KVKK</Link>
                    {' ve '}
                    <Link to="/aydinlatma-metni" className="text-blue-600 hover:text-blue-700">site kullanım koşullarını</Link>
                    {' kabul ediyorum'}
                  </label>
                </div>
                {validationErrors.kvkk && (
                  <p className="text-sm text-red-600">{validationErrors.kvkk}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                >
                  {loading ? 'Teklifler Alınıyor...' : 'Fiyat Al'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
      {!showQuotes && <TamamlayiciSaglikSeo />}
    </div>
  );
};

export default TamamlayiciSaglik;